import { Spinner } from '../../../../components/ui';
import React, { FunctionComponent, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import zapierLogo from '../../../../assets/images/integrations/zapier.svg';
import withAvailableIntegration from '../../../../hoc/withAvailableIntegration';
import withSubscriptionAwareness from '../../../../hoc/withSubscriptionAwareness';
import { useAccount } from '../../../../hooks/useAccount';
import { useGenerateZapierApiKey } from '../../../../hooks/useAccountMutations';
import { useAvailableIntegration } from '../../../../hooks/useAvailableIntegration';
import SettingsPageLayout from '../../../../layouts/settings/settingsPageLayout';
import { configuration } from '../../../../services/configuration';
import { ApiApp } from '../../../../services/models/api-app';
import { AppType } from '../../../../services/models/app-type';

const ZapierIntegrationPage: FunctionComponent = () => {
  const history = useHistory();

  const { account } = useAccount();
  const { generateZapierApiKey, isGeneratingZapierApiKey } = useGenerateZapierApiKey();
  const apiApps: ApiApp[] = account?.apiApps || [];
  const zapierApp = apiApps.find(x => x.type === AppType.Zapier);

  const isIntegrationAvailable = useAvailableIntegration(AppType.Zapier);

  useEffect(() => {
    const widget = document.createElement('script');
    widget.type = 'text/javascript';
    widget.async = true;
    widget.src = configuration.zapier.embeddedWidgetUrl;
    document.body.appendChild(widget);
  }, []);

  return (
    <SettingsPageLayout title="Zapier Integration | Newslit">
      <div className="mt-10 divide-y divide-gray-200">
        <div className="flex justify-between items-center py-3">
          <div className="space-y-1">
            <h3 className="text-lg leading-6 font-bold text-gray-900">Zapier Integration</h3>
            <p className="max-w-2xl text-sm text-gray-500 m-0">
              To integrate your Newslit News Brief with other services using Zapier, you will need
              to use the following key.
            </p>
          </div>
          <div className="w-16 shrink-0">
            <img className="h-10 p-1 object-contain" src={zapierLogo} alt="Zapier Logo" />
          </div>
        </div>

        <div className="mt-6">
          <dl className="">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="text-sm font-medium text-gray-500">Zapier Key</dt>
              <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="grow">
                  {isIntegrationAvailable && zapierApp && <code>{zapierApp.apiKey}</code>}
                </span>
                <span className="ml-4 shrink-0">
                  {!isIntegrationAvailable && (
                    <button
                      type="button"
                      className="bg-white rounded-md font-medium text-secondary-dark hover:text-secondary focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                      onClick={() => {
                        history.push('/dashboard/settings/pricing');
                      }}
                    >
                      Upgrade your plan
                    </button>
                  )}
                  {isIntegrationAvailable && !zapierApp && (
                    <button
                      type="button"
                      className="bg-white rounded-md font-medium text-secondary-dark hover:text-secondary focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                      onClick={() => generateZapierApiKey()}
                      disabled={isGeneratingZapierApiKey}
                    >
                      {isGeneratingZapierApiKey ? 'Generating Key' : 'Generate Key'}
                    </button>
                  )}
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="mt-10 divide-y divide-gray-200">
        <div className="space-y-1">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Popular Zaps</h3>
          <p className="max-w-2xl text-sm text-gray-500">
            Connect your Newslit Briefs to 2,000+ Apps using Zapier!
          </p>
        </div>
        <div className="mt-6 pt-3">
          <div id="js-zapier-integration-embed">
            <div className="loading-zapier">
              <Spinner />
            </div>
          </div>
        </div>
      </div>
    </SettingsPageLayout>
  );
};

export default withSubscriptionAwareness(
  withAvailableIntegration(AppType.Zapier, ZapierIntegrationPage)
);
